import { useState, type ElementType } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronRightCircle, Check, Pencil, Plus, BellPlus, CalendarPlus, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DateTimePicker } from '../ui/date-picker';
import { cn } from '../../lib/cn';
import { useStore } from '../../store/useStore';
import { leadService } from '../../lib/leadService';
import { googleCalendarUrl, icsDataUri } from '../../lib/calendar';
import { Lead, leadNextActionDue } from '../../types/leads.types';

// preferenza "usa Google Calendar" ricordata sul dispositivo (default: off)
const GCAL_PREF_KEY = 'lead_next_action_gcal';
const readGcalPref = () => {
    try { return localStorage.getItem(GCAL_PREF_KEY) === '1'; } catch { return false; }
};
const writeGcalPref = (v: boolean) => {
    try { localStorage.setItem(GCAL_PREF_KEY, v ? '1' : '0'); } catch { /* ignore */ }
};

// piccolo switch on/off inline (nessun componente Switch nel design system)
function Switch({ checked, onChange, label, icon: Icon }: {
    checked: boolean; onChange: (v: boolean) => void; label: string; icon?: ElementType;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className="flex items-center gap-2 text-sm text-gray-700"
        >
            <span className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                checked ? 'bg-blue-600' : 'bg-gray-300'
            )}>
                <span className={cn(
                    'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                    checked ? 'translate-x-4' : 'translate-x-0.5'
                )} />
            </span>
            {Icon && <Icon className="w-4 h-4 text-gray-500" />} {label}
        </button>
    );
}

// dettagli evento calendario a partire dai dati del lead
function calDetails(lead: Lead): string {
    const parts = [
        lead.full_name || lead.company_name || 'Lead',
        lead.phone ? `Tel: ${lead.phone}` : '',
        lead.email ? `Email: ${lead.email}` : '',
    ].filter(Boolean);
    return parts.join('\n');
}

// Blocco "Prossima azione": cosa fare + quando + promemoria (in-app e/o calendario).
export function LeadNextAction({ lead, onSaved }: { lead: Lead; onSaved: () => void }) {
    const { addNotification } = useStore();
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(lead.next_action || '');
    const [date, setDate] = useState<Date | undefined>(
        lead.next_action_at ? new Date(lead.next_action_at) : undefined
    );
    const [withReminder, setWithReminder] = useState(true);
    const [gcal, setGcal] = useState(readGcalPref); // usa Google Calendar (default off)
    const [saving, setSaving] = useState(false);

    const toggleGcal = (v: boolean) => { setGcal(v); writeGcalPref(v); };

    const overdue = leadNextActionDue(lead);

    const save = async () => {
        if (!text.trim()) {
            addNotification({ type: 'error', title: 'Scrivi cosa fare' });
            return;
        }
        setSaving(true);
        try {
            await leadService.updateLead(lead.id, {
                next_action: text.trim(),
                next_action_at: date ? date.toISOString() : null,
            });
            // promemoria in-app: evento 'meeting' programmato + reminder che fa scattare
            // audio/notifica (stesso sistema dei reminder della timeline).
            if (withReminder && date) {
                const ev = await leadService.createEvent({
                    lead_id: lead.id,
                    event_type: 'meeting',
                    title: text.trim(),
                    notes: null,
                    event_date: date.toISOString(),
                    status: 'open',
                });
                await leadService.createReminder({
                    event_id: ev.id,
                    remind_at: date.toISOString(),
                    is_completed: false,
                });
            }
            setEditing(false);
            onSaved();
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore salvataggio' });
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        setSaving(true);
        try {
            await leadService.updateLead(lead.id, { next_action: null, next_action_at: null });
            setText(''); setDate(undefined); setEditing(false);
            onSaved();
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore' });
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="mt-6 pt-4 border-t space-y-2">
                <label className="text-xs text-gray-400 block">Prossima azione</label>
                <Input
                    placeholder="Es. Richiamare per preventivo"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoFocus
                />
                <DateTimePicker date={date} setDate={setDate} placeholder="Quando (facoltativo)" />
                <label className={cn(
                    'flex items-center gap-2 text-sm cursor-pointer',
                    date ? 'text-gray-700' : 'text-gray-400'
                )}>
                    <input
                        type="checkbox"
                        checked={withReminder && !!date}
                        disabled={!date}
                        onChange={(e) => setWithReminder(e.target.checked)}
                    />
                    <BellPlus className="w-4 h-4" /> Crea promemoria in app all'orario indicato
                </label>
                <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={save} disabled={saving}>
                        {saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                        Annulla
                    </Button>
                </div>
            </div>
        );
    }

    const startDate = lead.next_action_at ? new Date(lead.next_action_at) : null;
    const calEvent = startDate
        ? { title: lead.next_action || 'Azione lead', start: startDate, details: calDetails(lead) }
        : null;

    return (
        <div className="mt-6 pt-4 border-t">
            <label className="text-xs text-gray-400 block mb-1">Prossima azione</label>
            {lead.next_action ? (
                <div className="space-y-2">
                    <div
                        className={cn(
                            'flex items-start justify-between gap-2 rounded-md px-2 py-1.5',
                            overdue ? 'bg-red-50' : 'bg-indigo-50'
                        )}
                    >
                        <div className={cn('flex items-start gap-1.5 min-w-0', overdue ? 'text-red-700' : 'text-indigo-700')}>
                            <ChevronRightCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-medium">{lead.next_action}</div>
                                {lead.next_action_at && (
                                    <div className="text-xs opacity-80">
                                        {format(new Date(lead.next_action_at), 'EEE d MMM yyyy, HH:mm', { locale: it })}
                                        {overdue && ' · scaduta'}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                            <button
                                title="Modifica"
                                onClick={() => { setText(lead.next_action || ''); setDate(lead.next_action_at ? new Date(lead.next_action_at) : undefined); setWithReminder(false); setEditing(true); }}
                                className="p-1 text-gray-400 hover:text-gray-700"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                title="Fatto / rimuovi"
                                onClick={clear}
                                disabled={saving}
                                className="p-1 text-gray-400 hover:text-green-600"
                            >
                                <Check className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {calEvent && (
                        <div className="space-y-1.5">
                            <Switch
                                checked={gcal}
                                onChange={toggleGcal}
                                label="Aggiungi a Google Calendar"
                                icon={CalendarPlus}
                            />
                            {gcal && (
                                <div className="flex flex-wrap gap-3 text-xs pl-11">
                                    <a
                                        href={googleCalendarUrl(calEvent)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                    >
                                        <CalendarPlus className="w-3.5 h-3.5" /> Apri Google Calendar
                                    </a>
                                    <a
                                        href={icsDataUri(calEvent)}
                                        download="promemoria-lead.ics"
                                        className="inline-flex items-center gap-1 text-gray-500 hover:underline"
                                    >
                                        <Download className="w-3.5 h-3.5" /> .ics (Apple/Outlook)
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <Button size="sm" variant="outline" onClick={() => { setWithReminder(true); setEditing(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Imposta prossima azione
                </Button>
            )}
        </div>
    );
}

export default LeadNextAction;
